import React, { useEffect, useMemo, useRef } from 'react';
import styled from 'styled-components';
import { useSubscription } from '@apollo/client';
import { listSubscription } from '../../../../graph/patients';
import { useScope } from '@/hooks/useScope';
import { playSound } from '@/components/pages/kiosk/monitor/hooks/useAlerts';
import waitingReady from '../../../../assets/sound/waitingReady.mp3';
import waitingRoomEmpty from '../../../../assets/images/waitingroom.svg';
import isMiddleEastHospital from '../../../../util/isMiddleEast';
import PanelEmpty from '../../../Panel/PanelEmpty';
import HospitalInfo from '../../../kiosk/HospitalInfo';
import ClientUpdater from '../../../ClientUpdater';
import PhaseColumn, { CARD_GAP_REM, COLUMN_GAP_REM, GRID_ROW_BODY } from './PhaseColumn';
import { useTimelinePageSize } from './useTimelinePageSize';
import {
  countReadyToSeeFamily,
  getCardFontSizeVw,
  getDensity,
  getVisiblePhases,
  groupPatientsByPhase,
  withDerivedFlags,
} from './timelinePhases';

/**
 * Trial "timeline" layout for the Waiting Room screen.
 *
 * Enabled per screen via the `waitingRoomTimelineView` screen configuration. Uses the same subscription,
 * derived flags, ready sound, empty state and footer as `WaitingRoomMonitor.jsx`, but lays
 * patients out under the phase they are currently in (Pre-Op → Surgery → Recovery → Post-Op).
 * Waiting Room patients are excluded. Deliberately shares no component code with the original
 * screen so the trial can evolve without touching it.
 */

const Root = styled.div`
  position: fixed;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  background-color: ${props => props.theme.backgroundColor.string()};
  color: ${props => props.theme.textColor.string()};
  overflow: hidden;
`;

/*
 * Three explicit rows: phase headers, patient cards, page dots. Each PhaseColumn drops its
 * pieces straight into these rows so all columns stay aligned.
 */
const Board = styled.div`
  flex: 1;
  min-height: 0;
  display: grid;
  grid-template-columns: repeat(${props => props.$columns}, minmax(0, 1fr));
  grid-template-rows: auto minmax(0, 1fr) auto;
  column-gap: ${COLUMN_GAP_REM}rem;
  padding: 0 1.5rem;

  @media (max-height: 900px) {
    padding: 0 1rem;
  }
`;

/* Full-board empty state: occupies the card row across every column, under the headers. */
const EmptyOverlay = styled.div`
  grid-row: ${GRID_ROW_BODY};
  grid-column: 1 / -1;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 0;
  pointer-events: none;
`;

const HospitalInfoWrapper = styled.div`
  padding-top: 1rem;
  flex: 0 0 auto;
  background-color: ${props => props.theme.textColor.alpha(0.07).string()};
`;

const remToPx = rem => {
  if (typeof window === 'undefined') return rem * 16;
  const base = parseFloat(window.getComputedStyle(document.documentElement).fontSize);
  return rem * (Number.isFinite(base) ? base : 16);
};

/**
 * Presentational board. Receives already-fetched patients so it can be rendered with mock data.
 */
export const WaitingRoomTimelineBoard = ({
  patients: rawPatients,
  loading,
  hasPostop,
  isMiddleEast,
  configuration,
}) => {
  const patients = useMemo(() => (rawPatients || []).map(withDerivedFlags), [rawPatients]);
  const phases = useMemo(() => getVisiblePhases(hasPostop), [hasPostop]);
  const grouped = useMemo(() => groupPatientsByPhase(patients, hasPostop), [patients, hasPostop]);

  const totalOnBoard = phases.reduce((sum, phase) => sum + grouped[phase.type].length, 0);
  const busiestColumn = phases.reduce((max, phase) => Math.max(max, grouped[phase.type].length), 0);

  const density = getDensity(busiestColumn);
  const cardFontSizeVw = getCardFontSizeVw(phases.length, density);
  // Headers follow the same scale as the cards but are capped so they stay a quiet "rail".
  const headerFontSizeVw = Math.min(cardFontSizeVw * 0.8, 1.9);

  /* ---- Ready-to-see-family sound (identical trigger to the original screen) ---- */
  const readyCount = countReadyToSeeFamily(patients);
  const previousReadyCount = useRef(null);
  useEffect(() => {
    if (!loading) {
      if (previousReadyCount.current !== null && readyCount > previousReadyCount.current) {
        playSound(waitingReady, configuration?.pacuReadySound);
      }
      previousReadyCount.current = readyCount;
    }
  }, [readyCount, loading, configuration?.pacuReadySound]);

  /* ---- Layout measurement ---- */
  const boardRef = useRef(null);
  const firstBodyRef = useRef(null);
  const pageSize = useTimelinePageSize({
    heightRef: firstBodyRef,
    cardsRootRef: boardRef,
    gapPx: remToPx(CARD_GAP_REM),
    deps: [totalOnBoard, density, phases.length, isMiddleEast],
  });

  return (
    <Root>
      <Board ref={boardRef} $columns={phases.length}>
        {phases.map((phase, idx) => (
          <PhaseColumn
            key={phase.type}
            phase={phase}
            index={idx}
            patients={grouped[phase.type]}
            pageSize={pageSize}
            isMiddleEast={isMiddleEast}
            cardFontSizeVw={cardFontSizeVw}
            headerFontSizeVw={headerFontSizeVw}
            hideEmptyState={totalOnBoard === 0}
            bodyRef={idx === 0 ? firstBodyRef : undefined}
          />
        ))}
        {totalOnBoard === 0 && (
          <EmptyOverlay>
            <PanelEmpty large image={waitingRoomEmpty} text="Waiting for patients…" />
          </EmptyOverlay>
        )}
      </Board>
      <HospitalInfoWrapper>
        <HospitalInfo />
      </HospitalInfoWrapper>
      {/* Only auto-update the kiosk while nothing is on the board, exactly like the original. */}
      {totalOnBoard <= 0 && <ClientUpdater />}
    </Root>
  );
};

/**
 * Container: subscribes to the same `waitingRoom` feed as the original screen and resolves the
 * hospital-level switches (Post-Op availability, Middle East labels).
 */
const WaitingRoomTimelineMonitor = ({ configuration }) => {
  const scope = useScope();
  const hospitalId = scope?.hospital?.id;

  // Same rule the internal monitor uses to decide whether Post-Op exists for this hospital.
  const hasPostop = !!scope?.hospital?.modules?.hasPostop && configuration?.showPostOp !== false;

  const { data, loading } = useSubscription(listSubscription);

  return (
    <WaitingRoomTimelineBoard
      patients={data?.waitingRoom}
      loading={loading}
      hasPostop={hasPostop}
      isMiddleEast={isMiddleEastHospital(hospitalId)}
      configuration={configuration}
    />
  );
};

export default WaitingRoomTimelineMonitor;
